import {
  createContext,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useContext,
  useId,
  useImperativeHandle,
  useRef,
} from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  dropdownChevronClassName,
  dropdownPlaceholderClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";
import type { SelectorFieldProps, SelectorTriggerProps } from "./types";

type SelectorFieldContextValue = {
  labelId?: string;
  descriptionId?: string;
  error?: boolean;
  required?: boolean;
};

const SelectorFieldContext = createContext<SelectorFieldContextValue>({});

export function SelectorField({
  label,
  hint,
  error,
  required,
  className,
  children,
}: SelectorFieldProps & { children: ReactNode }) {
  const message = typeof error === "string" ? error : hint;
  const generatedId = useId();
  const labelId = label ? `${generatedId}-label` : undefined;
  const descriptionId = message ? `${generatedId}-description` : undefined;

  return (
    <SelectorFieldContext.Provider
      value={{
        labelId,
        descriptionId,
        error: Boolean(error),
        required,
      }}
    >
      <div className={cn("space-y-1", className)}>
        {label ? (
          <div
            id={labelId}
            className="font-inter text-sm font-medium text-neutral-800"
          >
            {label}
            {required ? (
              <span aria-hidden className="ml-0.5 text-red-500">
                *
              </span>
            ) : null}
          </div>
        ) : null}
        {children}
        {message ? (
          <div
            id={descriptionId}
            role={error ? "alert" : undefined}
            className={cn(
              "font-inter text-sm leading-5 text-neutral-500",
              error && "text-red-500"
            )}
          >
            {message}
          </div>
        ) : null}
      </div>
    </SelectorFieldContext.Provider>
  );
}

export const SelectorTrigger = forwardRef<
  HTMLButtonElement,
  SelectorTriggerProps & { children: ReactNode }
>(function SelectorTrigger(
  {
    children,
    size = "md",
    open = false,
    disabled = false,
    error = false,
    clearable = false,
    onClear,
    placeholder,
    className,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    "data-state": dataState,
    ...props
  },
  ref
) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);
  const field = useContext(SelectorFieldContext);
  const describedBy = [ariaDescribedBy, field.descriptionId]
    .filter(Boolean)
    .join(" ");
  const labelledBy = [ariaLabelledBy, !ariaLabel ? field.labelId : undefined]
    .filter(Boolean)
    .join(" ");
  const clear = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClear?.();
    queueMicrotask(() => triggerRef.current?.focus());
  };

  return (
    <span className="relative block w-full">
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy || undefined}
        aria-describedby={describedBy || undefined}
        aria-invalid={(ariaInvalid ?? (error || field.error)) || undefined}
        aria-required={ariaRequired ?? field.required ?? undefined}
        data-placeholder={placeholder}
        data-state={open ? "open" : dataState}
        disabled={disabled}
        className={cn(
          "w-full justify-between gap-2 rounded-lg font-inter text-sm font-medium",
          size === "md" ? "h-10 px-3.5 py-2.5" : "h-8 px-3 py-1.5",
          dropdownTriggerClassName,
          "active:scale-100 disabled:bg-white disabled:opacity-40 data-[disabled]:bg-white data-[disabled]:opacity-40",
          error &&
            "border-red-500 focus-visible:border-red-500 focus-visible:shadow-[0_0_0_2px_rgba(239,68,68,0.08)] data-[state=open]:border-red-500",
          className
        )}
        {...props}
      >
        <span className="flex min-w-0 flex-1 items-center text-left">
          {children}
        </span>
        {clearable && onClear ? (
          <span aria-hidden className="size-5 shrink-0" />
        ) : null}
        <i
          aria-hidden
          className={cn(
            "fa-regular fa-chevron-down size-5 shrink-0 text-center text-[16px] leading-5 transition-transform",
            dropdownChevronClassName,
            open && "rotate-180"
          )}
        />
      </Button>
      {clearable && onClear ? (
        <button
          type="button"
          aria-label="Clear selection"
          disabled={disabled}
          className={cn(
            "absolute top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded p-0 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800 disabled:pointer-events-none",
            disabled && "opacity-40",
            size === "md" ? "right-[42px]" : "right-10"
          )}
          onClick={clear}
        >
          <i
            aria-hidden
            className="fa-regular fa-xmark size-5 shrink-0 text-center text-[16px] leading-5"
          />
        </button>
      ) : null}
    </span>
  );
});

export function SelectorPlaceholder({ children }: { children: ReactNode }) {
  return (
    <span className={cn("truncate", dropdownPlaceholderClassName)}>
      {children}
    </span>
  );
}
